"use client";
import { RefObject, useCallback, useEffect, useRef, useState } from "react";
import type { Map as LeafletMap, Marker } from "leaflet";
import { LGS } from "modules/lgs/model/lgs";

// Animation timing constants
const FLY_TO_DURATION_MS = 500;
const FLY_TO_DURATION_S = FLY_TO_DURATION_MS / 1000; // Leaflet flyTo expects seconds
const POPUP_DELAY_MS = FLY_TO_DURATION_MS + 100; // Open popup after pan completes

interface UseMapInteractionOptions {
  mapRef: RefObject<LeafletMap | null>;
  markerRefs?: RefObject<Map<number, Marker>>;
  onStoreSelect?: (store: LGS) => void;
}

export interface MapInteractionHandle {
  selectedStoreWotcId: number | null;
  selectStoreOnMap: (store: LGS) => void;
  selectStore: (wotcId: number) => void;
  clearSelection: () => void;
}

export function useMapInteraction({
  mapRef,
  markerRefs,
  onStoreSelect,
}: UseMapInteractionOptions): MapInteractionHandle {
  const [selectedStoreWotcId, setSelectedStoreWotcId] = useState<number | null>(
    null
  );
  const popupTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (popupTimeoutRef.current) {
        clearTimeout(popupTimeoutRef.current);
      }
    };
  }, []);

  const selectStoreOnMap = useCallback(
    (store: LGS) => {
      if (!mapRef.current) return;

      const map = mapRef.current;
      const coords: [number, number] = [store.latitude, store.longitude];

      // Clear any pending popup timeout
      if (popupTimeoutRef.current) {
        clearTimeout(popupTimeoutRef.current);
        popupTimeoutRef.current = null;
      }

      // Close any existing popup FIRST — this fires the popupclose event
      // which calls clearSelection → setSelectedStoreWotcId(null).
      // React batches both setState calls; the LAST one wins.
      map.closePopup();

      // Set selection AFTER closePopup so this is the last setState in the batch
      setSelectedStoreWotcId(store.wotcId);

      // Pan to store — offset the target north so the popup (which extends above
      // the marker) ends up centered in the viewport, preventing a second autoPan
      // shift after the popup opens.
      const POPUP_OFFSET_PX = 80;
      const targetZoom = 14;
      const point = map.project(coords, targetZoom);
      point.y -= POPUP_OFFSET_PX;
      const adjustedCenter = map.unproject(point, targetZoom);

      map.flyTo(adjustedCenter, targetZoom, {
        duration: FLY_TO_DURATION_S,
        easeLinearity: 0.25,
      });

      // Open the marker popup if markerRefs is available
      if (markerRefs?.current) {
        const marker = markerRefs.current.get(store.wotcId);
        if (marker) {
          // Small delay to let the map pan first
          // Capture marker reference to avoid race condition
          popupTimeoutRef.current = setTimeout(() => {
            // Temporarily disable autoPan so the popup doesn't trigger a
            // second pan after the flyTo already positioned the view.
            const popup = marker.getPopup();
            if (popup) {
              popup.options.autoPan = false;
              marker.openPopup();
              popup.options.autoPan = true;
            } else {
              marker.openPopup();
            }
            popupTimeoutRef.current = null;
          }, POPUP_DELAY_MS);
        }
      }

      // Call optional callback
      if (onStoreSelect) {
        onStoreSelect(store);
      }
    },
    [mapRef, markerRefs, onStoreSelect]
  );

  // Direct selection — only updates state, no flyTo or popup management.
  // Used for marker clicks where Leaflet handles popup natively.
  const selectStore = useCallback(
    (wotcId: number) => {
      setSelectedStoreWotcId(wotcId);
    },
    []
  );

  const clearSelection = useCallback(() => {
    setSelectedStoreWotcId(null);
  }, []);

  return {
    selectedStoreWotcId,
    selectStoreOnMap,
    selectStore,
    clearSelection,
  };
}
